import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded'
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded'
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded'
import { useAuth } from '../../context/useAuth'
import { useToast } from '../../context/useToast'
import { useFocusMode } from '../../context/useFocusMode'
import { MemberChatScreen } from '../Chat/MemberChat'
import { AISummaryBubble } from './SummaryBubble'
import { TaskCarousel } from './TaskCarousel'
import { BlockerReportSheet, type BlockerReportMode } from './BlockerReportSheet'
import { HelperCalendar } from './HelperCalendar'
import { HelperFieldTab } from './HelperFieldTab'
import { SchoolTab } from './School/SchoolTab'
import { HelperOpsTab } from './ops/HelperOpsTab'
import { HelperFieldSubtabStrip } from './HelperFieldSubtabStrip'
import { computeVisibleFieldSubtabs, type HelperFieldSubtab } from './helperFieldModel'
import { HelperFormsPanel } from './HelperFormsPanel'
import { MobileSyncCenter } from '../MobileSync/MobileSyncCenter'
import { MeetingActionGateway } from './MeetingActionGateway'
import { WorkspaceSwitcher } from './WorkspaceSwitcher'
import { HelperAppAccessBlockedNotice } from './HelperAppAccessBlockedNotice'
import { HelperMenuDrawer } from './HelperMenuDrawer'
import { HelperNotificationDrawer } from './HelperNotificationDrawer'
import { ChecklistDetailsModal } from './ChecklistDetailsModal'
import { HelperBottomNav } from './HelperBottomNav'
import { HelperProfilePanel } from '../Profile/HelperProfilePanel'
import { GhostEchoListener } from '../GhostEcho/GhostEchoListener'
import { openWorkspaceTarget, waitForFirstSnapshot } from '../../services/workspaceSwitch/openWorkspaceTarget'
import { BarcodeScannerShell } from './BarcodeScannerShell'
import { RaidCelebration } from '../Social/RaidCelebration'
import { RaidOverlay } from '../Raid/RaidOverlay'
import { ScreenGuideHost } from '../ScreenGuide'
import { TourGuideContext } from '../tourguide/tourGuideContextCore'
import { helperScreenGuideId } from './helperScreenGuides'
import { OverviewPhonePage } from '../Overview/OverviewPhonePage'
import { HelperSyncPill } from './HelperSyncPill'
import { IncomingInvitesPage } from '../Invites/IncomingInvitesPage'
import { Button, IconButton, Pressable } from '../Common'
import { MobileFullScreenPanel } from '../Common/MobileFullScreenPanel'
import { TabErrorBoundary } from '../Common/TabErrorBoundary'
import { MobileSettingsCommandCenter } from '../MobileSettings/MobileSettingsCommandCenter'
import { KeyboardHeightBridge } from '../../hooks/useKeyboardHeight'
import { HelperWidgetCachePublisher } from '../Widgets/NativeWidgetCachePublisher'
import { HelperApprovalsStrip } from './HelperApprovalsStrip'
import { HelperFirstRunLanguageModal } from './HelperFirstRunLanguageModal'
import { useNativeBackButton } from '../../hooks/useNativeBackButton'
import { ExitConfirmSheet } from '../Common/ExitConfirmSheet'
import { FocusModeOverlay } from '../Common/FocusMode/FocusModeOverlay'
import { useWorkspace } from '../../context/useWorkspace'
import { useChat } from '../../context/useChat'
import { useSharedDeviceClaim, getStoredSharedDeviceCredentials } from '../../services/opsSharedDevicesService'
import { useDisplayName } from '../../hooks/useDisplayName'
import { useNls } from '../../nls/nlsProvider'
import { useNativeWidgetIntent } from '../../hooks/useNativeWidgetIntent'
import { parseChecklistDeepLink } from '../../services/checklistDeepLinkService'
import { resolveProfileImageUrl } from '../../utils/profileImage'
import type { NativeWidgetIntent } from '../../services/nativeWidgetBridge'
import type { WorkspaceRole } from '../../types/workspace'
import {
  getHelperSnapshot,
  getHelperEduJobs,
  getHelperOpsPhoneItems,
  setHelperOpsPhoneItemsForWorkspace,
  clearHelperOpsPhoneItems,
  markHelperTabSeen,
  requestHelperWorkspaceSwitch,
  subscribeHelperStore,
  type HelperStoreSnapshot,
  type HelperTabKey,
} from '../../services/helper/helperStore'
import { subscribeToMyOpsPhoneItems } from '../../services/opsPhoneAccessService'
import { getSharedDeviceWorkRouterContext } from '../../services/sharedDeviceWorkRouter'
import { registerSwitchGuard } from '../../services/workspaceSwitch/switchGuards'
import { getCountDrafts, clearCountDrafts } from '../../services/helper/helperCountDraftStore'
import { subscribePageVisibility, usePageVisibility } from '../../services/pageVisibilityService'
import { submitStockCountRound } from '../../services/stockCountSuggestionService'
import type { ViewContext } from '../../services/ViewContextService'
import { resolveHelperNativeWidgetIntent, type HelperRouteView } from './helperShellRouting'
import { subscribeToHelperWorkspaces, updateHelperWorkspaceAppLocale, resolveFocusedChatItem } from '../../services/helper/helperWorkspaceService'
import { useHelperTaskFeed } from '../../services/helper/helperTaskService'
import type { HelperAssignedItem, WorkspaceMaskLookup } from '../../services/helper/helperAssignedItemsService'
import { mergeHelperVisibilityMask, DEFAULT_HELPER_VISIBILITY_MASK } from '../../services/helper/visibilityMask'
import { listPendingMutations, clearAll } from '../../services/helper/helperOutboxDb'
import { queueChecklistComment, queueChecklistProof, queueChecklistToggle } from '../../services/helper/queueService'
import { buildHelperReadinessModel } from './helperReadinessModel'
import { subscribeWorkspaceStock } from '../../services/stockService'
import { subscribeActiveQuotas } from '../../services/quotaService'
import {
  submitHelperBlockerReport,
  submitHelperReplenishmentRequest,
  subscribeOpenHelperBlockers,
} from '../../services/helper/helperBlockerService'
import {
  getActivePersonCache,
  subscribeActivePersonCache,
  updateActivePersonCache,
  type SharedDevicePersonSnapshot,
} from '../../services/sharedDevicePersonCache'
import { translateMessageForHelper } from '../../services/helper/helperChatAiService'
import {
  getHelperAppLocalePreference,
  getHelperLanguagePreference,
  hasHelperAppLocalePreference,
  normalizeHelperAppLocale,
  setHelperAppLocalePreference,
} from '../../services/helper/languageRegistryService'
import {
  dismissHelperFirstRunLanguagePrompt,
  getHelperFirstRunDefaultLocale,
  shouldShowHelperFirstRunLanguagePrompt,
} from './helperFirstRunLanguagePrompt'
import type { Message } from '../../types/chat'
import { registerMobilePushToken } from '../../services/mobilePushRegistrationService'
import type { HelperNotificationItem } from '../../services/helper/helperNotificationService'
import { subscribeToHelperMeetings } from '../../services/helper/helperMeetingService'
import type { HelperMeetingItem } from '../../services/helper/helperMeetingService'
import { submitHelperProof } from '../../services/helper/helperChecklistDetailsService'
import {
  InventoryScanError,
  ResourceMismatchError,
  type StockScanDirection,
} from '../../services/InventoryScannerService'
import { useStockScan } from '../../hooks/useStockScan'
import { emitSensoryEvent } from '../../services/aliveness/SensoryEventBus'
import { getDebugHelperChatMessages, isNoSignInPreviewActive, subscribeDebugChat } from '../../debug'
import type { Workspace } from '../../types/workspace'
import { MODULE_TOAST_DURATION_MS, switchedOffModulesFor } from '../../utils/workspaceModules'
import { hideFunPackNotification } from '../../utils/workspaceModulesNotifications'
import { hidePageNotification } from '../../utils/workspacePageNotifications'
import { hiddenSurfacesForModules, MODULE_TITLE_NLS_KEYS } from '../../utils/workspaceModulesSurfaces'
import type { StockItem } from '../../types/stock'
import type { Quota } from '../../types/quota'
import type { HelperBlockerReason, HelperOpenBlocker } from '../../types/helperBlocker'

type TabKey = HelperTabKey

const EMPTY_WORKSPACE_IDS: ReadonlySet<string> = new Set()

const PATH_BY_TAB: Record<HelperTabKey, string> = {
  TASKS: '/helper/dashboard',
  CALENDAR: '/helper/calendar',
  MEETINGS: '/helper/meetings',
  CHAT: '/helper/chat',
  FIELD: '/helper/field',
  SCHOOL: '/helper/school',
  OPS: '/helper/ops',
}

const HeaderIconButton = ({
  onClick,
  label,
  showDot,
  children,
  testId,
  unreadLabel,
}: {
  onClick: () => void
  label: string
  showDot?: boolean
  children: ReactNode
  testId?: string
  unreadLabel?: string
}) => (
  <IconButton
    variant="tertiary"
    size="md"
    onClick={onClick}
    label={label}
    className="relative"
    data-testid={testId}
    icon={(
      <>
        {children}
        {showDot ? (
          <span
            className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-[var(--danger)]"
            aria-label={unreadLabel ?? 'unread'}
          />
        ) : null}
      </>
    )}
  />
)

const itemMs = (iso: string | null | undefined): number => {
  if (!iso) return 0
  const parsed = Date.parse(iso)
  return Number.isNaN(parsed) ? 0 : parsed
}

/** Newness signal for the unread dot. Only the assignment/update timestamp
 * counts — future schedule dates are about when work happens, not when the
 * task became visible. */
const taskTimestamp = (item: HelperAssignedItem): number => itemMs(item.updatedAtIso)

export const HelperDashboardLayout = ({
  debugPanel,
  routeView = 'TASKS',
  sharedDeviceMode,
  deviceWorkspaceId,
}: {
  debugPanel?: ReactNode
  routeView?: HelperRouteView
  sharedDeviceMode?: boolean
  deviceWorkspaceId?: string
}) => {
  const claim = useSharedDeviceClaim()
  const isSharedDevice = Boolean(sharedDeviceMode || claim)

  const { locale: activeNlsLocale, setLocale, t } = useNls()
  const { currentUser, userProfile, logout } = useAuth()
  const { showToast } = useToast()
  const focusMode = useFocusMode()
  const { registerTaskFeed } = focusMode
  const [requestedTab, setRequestedTab] = useState<TabKey>(() => (
    isSharedDevice ? (routeView === 'OPS' ? 'OPS' : 'FIELD') : routeView === 'PROFILE' ? 'TASKS' : (routeView ?? 'TASKS')
  ))
  const [isProfileOpen, setIsProfileOpen] = useState(routeView === 'PROFILE')
  const helperName = useDisplayName(userProfile ?? currentUser)
  const profileImageUrl = resolveProfileImageUrl(userProfile, currentUser)
  const [snapshot, setSnapshot] = useState<HelperStoreSnapshot>(() => getHelperSnapshot())
  const effectiveActiveWorkspaceId = isSharedDevice && deviceWorkspaceId ? deviceWorkspaceId : snapshot.activeWorkspaceId
  const currentWorkspaceSummary = useMemo(
    () => snapshot.workspaces.find((w) => w.id === snapshot.activeWorkspaceId) ?? null,
    [snapshot.workspaces, snapshot.activeWorkspaceId],
  )
  const isAppAccessBlocked = Boolean(currentWorkspaceSummary && currentWorkspaceSummary.appAccess === false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [settingsInitialSection, setSettingsInitialSection] = useState<'ALERTS' | undefined>(undefined)
  const [detailsItem, setDetailsItem] = useState<HelperAssignedItem | null>(null)
  const [detailsOccurrenceKey, setDetailsOccurrenceKey] = useState<string | null>(null)
  const [latestMeetingMs, setLatestMeetingMs] = useState(0)
  const [helperMeetings, setHelperMeetings] = useState<HelperMeetingItem[]>([])
  const [latestChatMs, setLatestChatMs] = useState(0)
  const [ghostFocusItemId, setGhostFocusItemId] = useState<string | null>(null)
  const currentCarouselItemRef = useRef<HelperAssignedItem | null>(null)
  const [scannerItem, setScannerItem] = useState<HelperAssignedItem | null>(null)
  const [raidOverlayOpen, setRaidOverlayOpen] = useState(false)
  const [overviewOpen, setOverviewOpen] = useState(false)
  const [incomingInvitesOpen, setIncomingInvitesOpen] = useState(false)
  const [fieldSubtab, setFieldSubtab] = useState<HelperFieldSubtab>('TODAY')
  const [isSyncCenterOpen, setIsSyncCenterOpen] = useState(false)
  const [isFormsOpen, setIsFormsOpen] = useState(false)
  const [chatActionItemId, setChatActionItemId] = useState<string | null>(null)
  const [pendingChatChannelTarget, setPendingChatChannelTarget] = useState<{ workspaceId: string; channelId: string } | null>(null)
  const [stockItems, setStockItems] = useState<StockItem[]>([])
  const [activeQuotas, setActiveQuotas] = useState<Quota[]>([])
  const [openBlockers, setOpenBlockers] = useState<HelperOpenBlocker[]>([])
  const [blockerTarget, setBlockerTarget] = useState<{ item: HelperAssignedItem; mode: BlockerReportMode } | null>(null)
  const [blockerBusy, setBlockerBusy] = useState(false)
  const [blockerError, setBlockerError] = useState<string | null>(null)
  const [helperLocalePreferenceSeen, setHelperLocalePreferenceSeen] = useState(() => hasHelperAppLocalePreference())
  const [firstRunLanguagePromptHidden, setFirstRunLanguagePromptHidden] = useState(false)
  const { canPerform, activeWorkspaceId, workspaces: fullWorkspaces, getWorkspaceRole, rolesByWorkspaceId } = useWorkspace()
  const { setActiveChannelId } = useChat()
  const activeHelperWorkspace = useMemo(
    () => snapshot.workspaces.find((workspace) => workspace.id === effectiveActiveWorkspaceId) ?? null,
    [effectiveActiveWorkspaceId, snapshot.workspaces],
  )

  useEffect(() => {
    if (!pendingChatChannelTarget) return
    if (!snapshot.activeWorkspaceId || activeWorkspaceId !== pendingChatChannelTarget.workspaceId) return
    if (snapshot.activeWorkspaceId !== pendingChatChannelTarget.workspaceId) return
    setActiveChannelId(pendingChatChannelTarget.channelId)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Clears pending channel target once target workspace matches
    setPendingChatChannelTarget(null)
  }, [activeWorkspaceId, pendingChatChannelTarget, setActiveChannelId, snapshot.activeWorkspaceId])

  const setActiveTab = (tab: TabKey) => {
    const targetTab = isSharedDevice ? (tab === 'OPS' ? 'OPS' : 'FIELD') : tab
    markHelperTabSeen(targetTab)
    setIsProfileOpen(false)
    setRequestedTab(targetTab)
    if (typeof window !== 'undefined' && window.location.pathname !== PATH_BY_TAB[targetTab]) {
      window.history.pushState({}, '', PATH_BY_TAB[targetTab])
    }
  }

  const shellRef = useRef<HTMLDivElement>(null)
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false)
  const [unsentConfirmOpen, setUnsentConfirmOpen] = useState(false)
  const [unsentCount, setUnsentCount] = useState(0)

  const handleSignOut = useCallback(async () => {
    if (isSharedDevice) {
      if (currentUser?.uid && snapshot.activeWorkspaceId) {
        clearCountDrafts(currentUser.uid, snapshot.activeWorkspaceId)
      }
      clearHelperOpsPhoneItems(snapshot.activeWorkspaceId || undefined)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('shared-device:switch-out', { detail: { reason: 'SWITCH' } }))
      }
      await logout()
      return
    }
    try {
      const pending = await listPendingMutations()
      if (pending.length > 0) {
        setUnsentCount(pending.length)
        setUnsentConfirmOpen(true)
        return
      }
    } catch {
      // If outbox is unbound or fails, proceed to logout
    }
    await logout()
  }, [isSharedDevice, currentUser, snapshot.activeWorkspaceId, logout])

  const handleConfirmSignOutWithUnsent = useCallback(async () => {
    try {
      await clearAll()
    } catch {
      // ignore
    }
    setUnsentConfirmOpen(false)
    await logout()
  }, [logout])

  useEffect(() => {
    return registerSwitchGuard({
      id: 'helper-count',
      describe: (from, to) => {
        if (!currentUser?.uid || !from || from === to) return null
        const drafts = getCountDrafts(currentUser.uid, from)
        if (drafts.length === 0) return null
        return t('NLSHelperSwitchGuardCountDraftsBodyWithCount', { count: drafts.length })
      },
      save: async (from) => {
        if (!currentUser?.uid || !from) return
        const drafts = getCountDrafts(currentUser.uid, from)
        if (drafts.length === 0) return
        const actor = {
          id: currentUser.uid,
          name: userProfile?.displayName || currentUser.displayName || 'Helper',
        }
        await submitStockCountRound({
          workspaceId: from,
          actor,
          suggestions: drafts.map((draft) => ({
            stockItemId: draft.stockItemId,
            sku: draft.sku,
            barcode: draft.barcode,
            countedQuantity: draft.countedQuantity,
            systemQuantityAtScan: draft.systemQuantityAtScan,
            note: draft.note,
          })),
        })
        clearCountDrafts(currentUser.uid, from)
        showToast(t('NLSHelperStockCountQueued'), 'success')
      },
      discard: (from) => {
        if (currentUser?.uid && from) {
          clearCountDrafts(currentUser.uid, from)
        }
      },
    })
  }, [currentUser, userProfile, showToast, t])

  const [measuredBottomChromeHeight, setMeasuredBottomChromeHeight] = useState<number | null>(null)

  const handleBottomNavHeightChange = useCallback((height: number) => {
    setMeasuredBottomChromeHeight(height)
    shellRef.current?.style.setProperty('--helper-bottom-chrome-height', `${Math.ceil(height)}px`)
  }, [])

  useNativeBackButton({
    onEmpty: () => {
      if (focusMode.active) {
        focusMode.exit()
        return
      }
      if (activeTab !== (isSharedDevice ? 'FIELD' : 'TASKS')) {
        setActiveTab(isSharedDevice ? 'FIELD' : 'TASKS')
        return
      }
      setExitConfirmOpen(true)
    },
  })

  const openSyncCenter = () => {
    setIsSyncCenterOpen(true)
  }

  useEffect(() => {
    if (routeView === 'PROFILE') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Synchronizes routeView prop to profile and tab state
      setIsProfileOpen(true)
      return
    }
    setIsProfileOpen(false)
    setRequestedTab(routeView)
    markHelperTabSeen(routeView)
  }, [routeView])

  const hasEduJobs = useMemo(() => {
    const jobs = snapshot.activeWorkspaceId
      ? (snapshot.eduJobsByWorkspace[snapshot.activeWorkspaceId] ?? getHelperEduJobs(snapshot.activeWorkspaceId))
      : snapshot.eduJobs
    return Boolean(jobs && jobs.length > 0)
  }, [snapshot.activeWorkspaceId, snapshot.eduJobs, snapshot.eduJobsByWorkspace])

  const hasOpsItems = useMemo(() => {
    const wsId = effectiveActiveWorkspaceId ?? snapshot.activeWorkspaceId
    const items = wsId
      ? (snapshot.opsPhoneItemsByWorkspace?.[wsId] ?? getHelperOpsPhoneItems(wsId))
      : snapshot.opsPhoneItems
    return Boolean(items && items.length > 0)
  }, [effectiveActiveWorkspaceId, snapshot.activeWorkspaceId, snapshot.opsPhoneItems, snapshot.opsPhoneItemsByWorkspace])

  const activeTab = isSharedDevice
    ? (requestedTab === 'OPS' && hasOpsItems ? 'OPS' : 'FIELD')
    : requestedTab === 'SCHOOL' && !hasEduJobs
      ? 'TASKS'
      : requestedTab === 'OPS' && !hasOpsItems
        ? 'TASKS'
        : requestedTab
  const tourGuide = useContext(TourGuideContext)
  const isTourActive = Boolean(tourGuide?.isTourActive)
  const hasOverlayWithGuide = overviewOpen || incomingInvitesOpen || isProfileOpen || settingsOpen
  const activeScreenGuideId = hasOverlayWithGuide ? undefined : helperScreenGuideId(activeTab)

  useEffect(() => {
    if (requestedTab === 'SCHOOL' && !hasEduJobs) {
      if (typeof window !== 'undefined' && window.location.pathname === PATH_BY_TAB.SCHOOL) {
        window.history.replaceState({}, '', PATH_BY_TAB.TASKS)
      }
    }
    if (requestedTab === 'OPS' && !hasOpsItems) {
      if (typeof window !== 'undefined' && window.location.pathname === PATH_BY_TAB.OPS) {
        window.history.replaceState({}, '', PATH_BY_TAB.TASKS)
      }
    }
  }, [requestedTab, hasEduJobs, hasOpsItems])

  useEffect(() => {
    const wsId = effectiveActiveWorkspaceId
    const uid = (isSharedDevice ? getSharedDeviceWorkRouterContext()?.personUid : undefined) || currentUser?.uid
    if (!wsId || !uid) return
    const activeWs = fullWorkspaces.find((w) => w.id === wsId)
    const modules = activeWs?.modules as unknown as Record<string, boolean | undefined> | undefined
    const role = (activeHelperWorkspace?.role as WorkspaceRole | undefined) ||
      (getWorkspaceRole ? (getWorkspaceRole(wsId) as WorkspaceRole | undefined) : undefined) ||
      (rolesByWorkspaceId?.get(wsId) as WorkspaceRole | undefined)
    if (!role) {
      if (getHelperOpsPhoneItems(wsId).length > 0) {
        setHelperOpsPhoneItemsForWorkspace(wsId, [])
      }
      return
    }
    const unsub = subscribeToMyOpsPhoneItems(
      wsId,
      uid,
      role,
      modules,
      (items) => {
        setHelperOpsPhoneItemsForWorkspace(wsId, items)
      },
    )
    return () => unsub()
  }, [
    effectiveActiveWorkspaceId,
    isSharedDevice,
    currentUser?.uid,
    fullWorkspaces,
    activeHelperWorkspace?.role,
    getWorkspaceRole,
    rolesByWorkspaceId,
  ])

  useEffect(() => subscribeHelperStore(setSnapshot), [])

  useEffect(() => {
    const workspaceLocale = activeHelperWorkspace?.helperAppLocale ?? null
    const hasLocalPreference = hasHelperAppLocalePreference()
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Synchronizes local preference to state on workspace change
    if (hasLocalPreference) setHelperLocalePreferenceSeen(true)
    const locale = normalizeHelperAppLocale(workspaceLocale ?? (hasLocalPreference ? getHelperAppLocalePreference() : 'en'))
    if (workspaceLocale || hasLocalPreference) setHelperAppLocalePreference(locale)
    setLocale(locale)
  }, [activeHelperWorkspace?.helperAppLocale, activeHelperWorkspace?.id, setLocale])

  // Mark the default-active tab as seen on first mount so the dot doesn't
  // linger on Tasks while the helper is already looking at it.
  useEffect(() => {
    markHelperTabSeen('TASKS')
  }, [])

  // Subscribe to meeting + chat sources passively (without rendering) so the
  // bottom-nav unread dot can light up even on tabs the helper isn't on.
  useEffect(() => {
    if (isSharedDevice || !snapshot.activeWorkspaceId || !currentUser?.uid) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Resets meetings state when active workspace or user is cleared
      setLatestMeetingMs(0)
      setHelperMeetings([])
      return
    }
    // Use the subscription firing as the "this is the latest snapshot" signal —
    // the meeting payload's `startsAt` is when the meeting happens, not when it
    // landed in the helper's view, so it can't be used for unread tracking.
    return subscribeToHelperMeetings(snapshot.activeWorkspaceId, currentUser.uid, (items) => {
      setHelperMeetings(items)
      setLatestMeetingMs(items.length > 0 ? Date.now() : 0)
    })
  }, [isSharedDevice, snapshot.activeWorkspaceId, currentUser?.uid])

  useEffect(() => {
    if (isSharedDevice) return
    const refresh = () => {
      if (!isNoSignInPreviewActive()) return
      const messages = getDebugHelperChatMessages()
      const max = messages.reduce((acc, message) => Math.max(acc, itemMs(message.timestampIso)), 0)
      setLatestChatMs(max)
    }
    refresh()
    if (!isNoSignInPreviewActive()) return
    return subscribeDebugChat(refresh)
  }, [isSharedDevice, snapshot.activeWorkspaceId])

  useEffect(() => {
    if (!currentUser?.uid) return
    return subscribeToHelperWorkspaces(currentUser.uid)
  }, [currentUser?.uid])

  useEffect(() => {
    if (isSharedDevice || !currentUser?.uid) return
    void registerMobilePushToken(currentUser.uid, 'HELPER')
  }, [isSharedDevice, currentUser?.uid])

  const handleTaskFeedError = useCallback((err: unknown) => {
    console.warn('[helperTaskFeed] subscription failed', err)
    showToast("Couldn't load checklists — you may not have access. Contact your workspace admin.", 'error')
  }, [showToast])

  const workspaceMaskLookup = useCallback<WorkspaceMaskLookup>((workspaceId: string) => {
    const ws = snapshot.workspaces.find((w) => w.id === workspaceId)
    return mergeHelperVisibilityMask(DEFAULT_HELPER_VISIBILITY_MASK, ws?.helperVisibilityMaskDefault)
  }, [snapshot.workspaces])

  const [offlineCacheSnapshot, setOfflineCacheSnapshot] = useState<SharedDevicePersonSnapshot | null>(
    () => getActivePersonCache()?.snapshot ?? null,
  )

  useEffect(() => {
    return subscribeActivePersonCache((snap) => {
      setOfflineCacheSnapshot(snap)
    })
  }, [])

  const taskFeed = useHelperTaskFeed(currentUser?.uid ?? null, workspaceMaskLookup, handleTaskFeedError)

  const effectiveFeedItems = useMemo(() => {
    if (isSharedDevice && !currentUser && offlineCacheSnapshot) {
      return offlineCacheSnapshot.todayItems
    }
    return taskFeed.items
  }, [isSharedDevice, currentUser, offlineCacheSnapshot, taskFeed.items])

  useEffect(() => {
    if (!isSharedDevice || !currentUser) return
    const timer = setTimeout(() => {
      if (getActivePersonCache()) {
        void updateActivePersonCache((prev) => ({
          ...prev,
          todayItems: taskFeed.items,
        }))
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [isSharedDevice, currentUser, taskFeed.items])

  const [taskFilter, setTaskFilter] = useState<'ALL' | 'ACTIVE' | 'UPCOMING' | 'PAST'>('ACTIVE')

  const taskCounts = useMemo(() => {
    let active = 0
    let upcoming = 0
    let past = 0
    for (const item of effectiveFeedItems) {
      const time = item.timeStatus ?? 'active'
      if (time === 'upcoming') upcoming++
      else if (time === 'archived') past++
      else active++
    }
    return { all: effectiveFeedItems.length, active, upcoming, past }
  }, [effectiveFeedItems])

  const filteredTasks = useMemo(() => {
    if (taskFilter === 'ALL') return effectiveFeedItems
    return effectiveFeedItems.filter((item) => {
      const time = item.timeStatus ?? 'active'
      if (taskFilter === 'UPCOMING') return time === 'upcoming'
      if (taskFilter === 'PAST') return time === 'archived'
      return time === 'active'
    })
  }, [effectiveFeedItems, taskFilter])

  const openTodos = useCallback(() => {
    if (!isSharedDevice) {
      setActiveTab('TASKS')
    }
  }, [isSharedDevice])

  useEffect(() => {
    registerTaskFeed({ items: filteredTasks, openTodos })
  }, [filteredTasks, openTodos, registerTaskFeed])

  const activeFullWorkspace = useMemo(
    () => fullWorkspaces.find((workspace) => workspace.id === snapshot.activeWorkspaceId) ?? null,
    [fullWorkspaces, snapshot.activeWorkspaceId],
  )
  const readinessWorkspace = useMemo(() => {
    if (activeFullWorkspace) return activeFullWorkspace
    if (!activeHelperWorkspace) return null
    return {
      id: activeHelperWorkspace.id,
      name: activeHelperWorkspace.name,
      slug: activeHelperWorkspace.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      plan: 'GROWTH_TIER',
      kind: 'TEAM',
      ownerId: '',
      isPersonal: false,
      modules: {
        stockEnabled: true,
        quotaEnabled: true,
        budgetEnabled: false,
        helperScanEnabled: true,
        helperCountEnabled: true,
        helperFormsEnabled: true,
      },
      settings: { currency: 'USD' },
    } as Workspace
  }, [activeFullWorkspace, activeHelperWorkspace])
  const switchedOffModules = useMemo(
    () => switchedOffModulesFor(readinessWorkspace),
    [readinessWorkspace],
  )
  const moduleHidden = useMemo(
    () => hiddenSurfacesForModules(switchedOffModules),
    [switchedOffModules],
  )
  const pageVisibility = usePageVisibility()

  useEffect(() => {
    if (isSharedDevice || !snapshot.activeWorkspaceId) return
    return subscribePageVisibility(snapshot.activeWorkspaceId, currentUser?.uid, false, 'HELPER')
  }, [isSharedDevice, snapshot.activeWorkspaceId, currentUser?.uid])

  const helperFunPackOff = switchedOffModules.has('funPack')
  // Helper workspace summaries carry no modules, so the active workspace's switch decides for every item.
  const hideNotificationItem = useCallback(
    (item: HelperNotificationItem) =>
      hideFunPackNotification(
        { eventType: item.eventType, category: item.type, workspaceId: null },
        { funPackOffWorkspaceIds: EMPTY_WORKSPACE_IDS, activeWorkspaceFunPackOff: helperFunPackOff },
      ) ||
      hidePageNotification({
        eventType: item.eventType,
        category: item.type,
        workspaceId: null,
      }, {
        controls: pageVisibility.effectiveControls,
        isOwner: pageVisibility.isOwner,
        role: pageVisibility.role,
      }),
    [helperFunPackOff, pageVisibility],
  )

  useEffect(() => {
    if (moduleHidden.helperTabs.has(activeTab)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Resets active tab to TASKS when hidden by workspace module settings
      setActiveTab(isSharedDevice ? 'FIELD' : 'TASKS')
    }
  }, [moduleHidden.helperTabs, activeTab, isSharedDevice])

  useEffect(() => {
    if (pageVisibility(activeTab)) {
      queueMicrotask(() => {
        setActiveTab(isSharedDevice ? 'FIELD' : 'TASKS')
        showToast(t('NLSControlNoticePageHidden'), 'info')
      })
    }
  }, [activeTab, pageVisibility, showToast, t, isSharedDevice])
  const visibleFieldSubtabs = useMemo(
    () => computeVisibleFieldSubtabs(readinessWorkspace?.modules),
    [readinessWorkspace?.modules],
  )

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Resets field subtab to TODAY when hidden by modules
    if (!visibleFieldSubtabs.includes(fieldSubtab)) setFieldSubtab('TODAY')
  }, [visibleFieldSubtabs, fieldSubtab])

  const taskItemIdsKey = useMemo(() => taskFeed.items.map((item) => item.id).join('|'), [taskFeed.items])

  useEffect(() => {
    if (!effectiveActiveWorkspaceId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Resets stock items when workspace is cleared
      setStockItems([])
      return
    }
    return subscribeWorkspaceStock(effectiveActiveWorkspaceId, setStockItems, () => setStockItems([]))
  }, [effectiveActiveWorkspaceId])

  useEffect(() => {
    if (isSharedDevice || !snapshot.activeWorkspaceId || !currentUser?.uid) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Resets quotas when workspace or user is cleared
      setActiveQuotas([])
      return
    }
    return subscribeActiveQuotas(snapshot.activeWorkspaceId, currentUser.uid, setActiveQuotas, () => setActiveQuotas([]))
  }, [isSharedDevice, currentUser?.uid, snapshot.activeWorkspaceId])

  useEffect(() => {
    if (isSharedDevice || !snapshot.activeWorkspaceId || !currentUser?.uid || taskFeed.items.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Resets open blockers when workspace, user, or tasks are empty
      setOpenBlockers([])
      return
    }
    const itemIds = taskItemIdsKey ? taskItemIdsKey.split('|') : []
    return subscribeOpenHelperBlockers(snapshot.activeWorkspaceId, currentUser.uid, itemIds, setOpenBlockers, () => setOpenBlockers([]))
  }, [isSharedDevice, currentUser?.uid, snapshot.activeWorkspaceId, taskFeed.items.length, taskItemIdsKey])

  const readinessByItemId = useMemo(() => {
    const byItem = new Map<string, HelperOpenBlocker[]>()
    for (const blocker of openBlockers) {
      byItem.set(blocker.itemId, [...(byItem.get(blocker.itemId) ?? []), blocker])
    }
    return new Map(taskFeed.items.map((item) => [item.id, buildHelperReadinessModel({
      item,
      workspace: readinessWorkspace,
      stockItems,
      quotas: activeQuotas,
      openBlockers: byItem.get(item.id) ?? [],
    })]))
  }, [activeQuotas, openBlockers, readinessWorkspace, stockItems, taskFeed.items])

  const closeBlockerSheet = () => {
    if (blockerBusy) return
    setBlockerTarget(null)
    setBlockerError(null)
  }

  const submitBlockerSheet = async ({ reason, note }: { reason: HelperBlockerReason; note: string }) => {
    if (!actor || !blockerTarget) return
    const readiness = readinessByItemId.get(blockerTarget.item.id) ?? null
    setBlockerBusy(true)
    setBlockerError(null)
    try {
      if (blockerTarget.mode === 'REPLENISHMENT') {
        await submitHelperReplenishmentRequest({
          item: blockerTarget.item,
          actor,
          stockItemIds: readiness?.stockItemIds ?? [],
          shortagesById: readiness?.stockShortagesById ?? {},
          note,
        })
      } else {
        await submitHelperBlockerReport({
          item: blockerTarget.item,
          actor,
          reason,
          note,
        })
      }
      setBlockerTarget(null)
      showToast(t('NLSHelperBlockerQueued'), 'success')
    } catch (error) {
      const message = error instanceof Error ? error.message : t('NLSHelperBlockerQueueFailed')
      setBlockerError(message)
      showToast(message, 'error')
    } finally {
      setBlockerBusy(false)
    }
  }

  const unreadByTab = useMemo(() => {
    const { lastSeenByTab } = snapshot
    const tasksLatest = taskFeed.items.reduce((acc, item) => Math.max(acc, taskTimestamp(item)), 0)
    return {
      TASKS: tasksLatest > lastSeenByTab.TASKS,
      CALENDAR: tasksLatest > lastSeenByTab.CALENDAR,
      MEETINGS: latestMeetingMs > lastSeenByTab.MEETINGS,
      CHAT: latestChatMs > lastSeenByTab.CHAT,
      FIELD: false,
      SCHOOL: false,
      OPS: false,
    }
  }, [snapshot, taskFeed.items, latestMeetingMs, latestChatMs])

  const actor = currentUser
    ? {
        id: currentUser.uid,
        name: helperName,
      }
    : null

  const handleHelperAppLocaleChange = useCallback(async (localeInput: string) => {
    const locale = setHelperAppLocalePreference(localeInput)
    setHelperLocalePreferenceSeen(true)
    setLocale(locale)
    if (isSharedDevice || !currentUser?.uid || !snapshot.activeWorkspaceId) return
    await updateHelperWorkspaceAppLocale({
      workspaceId: snapshot.activeWorkspaceId,
      uid: currentUser.uid,
      locale,
    })
  }, [isSharedDevice, currentUser, setLocale, snapshot.activeWorkspaceId])

  const showFirstRunLanguagePrompt = !isSharedDevice && !firstRunLanguagePromptHidden && shouldShowHelperFirstRunLanguagePrompt({
    activeWorkspaceId: snapshot.activeWorkspaceId,
    workspacesLoading: snapshot.workspacesLoading,
    helperAppLocale: activeHelperWorkspace?.helperAppLocale ?? null,
    hasLocalPreference: helperLocalePreferenceSeen,
  })

  const completeFirstRunLanguageSelection = useCallback(async (localeInput: string) => {
    await handleHelperAppLocaleChange(localeInput)
    dismissHelperFirstRunLanguagePrompt()
    setFirstRunLanguagePromptHidden(true)
  }, [handleHelperAppLocaleChange])

  const skipFirstRunLanguageSelection = useCallback(() => {
    const locale = setHelperAppLocalePreference(getHelperFirstRunDefaultLocale(activeHelperWorkspace?.helperAppLocale))
    setHelperLocalePreferenceSeen(true)
    setLocale(locale)
    dismissHelperFirstRunLanguagePrompt()
    setFirstRunLanguagePromptHidden(true)
  }, [activeHelperWorkspace?.helperAppLocale, setLocale])

  const openItemById = useCallback(async (itemId: string, periodKey?: string | null, targetWorkspaceId?: string | null) => {
    if (targetWorkspaceId && targetWorkspaceId !== snapshot.activeWorkspaceId) {
      await waitForFirstSnapshot(targetWorkspaceId, 4000)
    }
    const match = effectiveFeedItems.find((entry) => entry.id === itemId)
    if (match) {
      setDetailsOccurrenceKey(periodKey ?? null)
      setDetailsItem(match)
    }
  }, [snapshot.activeWorkspaceId, effectiveFeedItems])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const handlePopState = () => {
      const checklistLink = parseChecklistDeepLink(window.location)
      if (checklistLink) {
        openItemById(checklistLink.itemId, checklistLink.periodKey)
        if (!isSharedDevice) {
          setActiveTab('TASKS')
        }
        try {
          const url = new URL(window.location.href)
          url.searchParams.delete('item')
          url.searchParams.delete('day')
          url.searchParams.delete('week')
          url.searchParams.delete('ws')
          url.searchParams.delete('workspaceId')
          const nextSearch = url.searchParams.toString()
          const nextUrl = url.pathname + (nextSearch ? `?${nextSearch}` : '') + url.hash
          window.history.replaceState(window.history.state, '', nextUrl)
        } catch {
          // ignore
        }
      }
    }
    handlePopState()
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [openItemById, isSharedDevice])

  const handleNativeWidgetIntent = useCallback((intent: NativeWidgetIntent) => {
    const resolution = resolveHelperNativeWidgetIntent(intent, effectiveFeedItems, snapshot.activeWorkspaceId)
    if (resolution.kind === 'OPEN_TAB') {
      if (resolution.fieldSubtab) setFieldSubtab(resolution.fieldSubtab)
      else if (resolution.controlsSubtab) setFieldSubtab(resolution.controlsSubtab as HelperFieldSubtab)
      setActiveTab(resolution.tab)
      return
    }
    const item = effectiveFeedItems.find((entry) => entry.id === resolution.itemId)
    setActiveTab(resolution.tab)
    if (item && resolution.mode === 'SCANNER') {
      setScannerItem(item)
    } else if (item) {
      setDetailsItem(item)
    }
  }, [snapshot.activeWorkspaceId, effectiveFeedItems])

  useNativeWidgetIntent(handleNativeWidgetIntent)

  const handleNotificationNavigate = (item: HelperNotificationItem) => {
    const kind = item.deeplink?.kind
    if (kind === 'CHECKLIST' && item.deeplink?.refId) {
      openItemById(item.deeplink.refId, item.periodKey)
      if (!isSharedDevice) {
        setActiveTab('TASKS')
      }
    } else if (kind === 'MEETING') {
      if (switchedOffModules.has('meetings')) {
        showToast(
          t('NLSWorkspaceModuleTurnedOffToast', { module: t(MODULE_TITLE_NLS_KEYS.meetings) }),
          'info',
          undefined,
          'normal',
          { durationMs: MODULE_TOAST_DURATION_MS },
        )
        return
      }
      setActiveTab('MEETINGS')
    } else if (kind === 'CHAT') {
      const channelId = item.chatTarget?.channelId
        ?? (item.deeplink?.kind === 'CHAT' ? item.deeplink.refId : null)
      const workspaceId = item.workspaceId ?? snapshot.activeWorkspaceId
      if (channelId && workspaceId) {
        setPendingChatChannelTarget({ workspaceId, channelId })
      }
      setActiveTab('CHAT')
    } else if (kind === 'INVITE' || item.type === 'INVITE_RECEIVED') {
      setIncomingInvitesOpen(true)
    }
  }

  const applyGhostEcho = useCallback((context: ViewContext) => {
    if (!snapshot.workspaces.some((workspace) => workspace.id === context.workspaceId)) return 'UNAVAILABLE' as const
    let targetItem: HelperAssignedItem | null = null
    if (context.taskId) {
      const item = effectiveFeedItems.find((entry) => entry.id === context.taskId && entry.workspaceId === context.workspaceId)
      if (!item) return 'UNAVAILABLE' as const
      targetItem = item
    }
    void openWorkspaceTarget({
      workspaceId: context.workspaceId,
      source: 'GHOST_ECHO',
      target: context.kind === 'CHAT' ? 'chat' : context.taskId ? `checklist/${context.taskId}` : undefined,
      options: {
        activeWorkspaceId: snapshot.activeWorkspaceId,
        workspaces: snapshot.workspaces,
        switchWorkspace: async (targetId) => {
          return await requestHelperWorkspaceSwitch(targetId)
        },
        showToast,
        t,
      },
      open: () => {
        if (context.kind === 'CHAT') {
          setActiveTab('CHAT')
          return
        }
        if (targetItem) {
          if (!isSharedDevice) {
            setActiveTab('TASKS')
          }
          setGhostFocusItemId(targetItem.id)
          window.setTimeout(() => setGhostFocusItemId((current) => current === targetItem.id ? null : current), 2_000)
          return
        }
        if (!isSharedDevice) {
          setActiveTab('TASKS')
        }
      },
    })
    return 'APPLIED' as const
  }, [snapshot.activeWorkspaceId, snapshot.workspaces, effectiveFeedItems, isSharedDevice, showToast, t])

  const { scanStock } = useStockScan({
    workspaceId: scannerItem?.workspaceId ?? null,
    actor: currentUser ? { id: currentUser.uid, name: helperName || currentUser.email || t('NLSHelperPanelProfileEyebrow'), role: 'HELPER' } : null,
    mode: 'helper',
  })

  const handleInventoryScan = useCallback(async (barcode: string, direction: StockScanDirection = 'OUT') => {
    if (!currentUser || !scannerItem) return
    const taskId = typeof scannerItem.parentContainerId === 'string' ? scannerItem.parentContainerId : ''
    const controller = new AbortController()
    emitSensoryEvent('MUTATION_TRIGGERED', { source: 'inventory-scan', targetId: scannerItem.id }, { signal: controller.signal })
    try {
      const result = await scanStock(barcode, direction, {
        taskId,
        checklistItemId: scannerItem.id,
      })
      setScannerItem(null)
      setGhostFocusItemId(scannerItem.id)
      window.setTimeout(() => setGhostFocusItemId((current) => current === scannerItem.id ? null : current), 2_000)
      const toastMessage = result.action === 'IN'
        ? t('NLSHelperToastScannedIn', { name: result.item.name })
        : t('NLSHelperToastScannedOut', { name: result.item.name })
      emitSensoryEvent('SCAN_SUCCESS', { source: 'inventory-scan', targetId: scannerItem.id }, { signal: controller.signal })
      showToast(toastMessage, 'ghost')
    } catch (error) {
      controller.abort()
      emitSensoryEvent('SCAN_ERROR', { source: 'inventory-scan', targetId: scannerItem.id })
      const message = error instanceof ResourceMismatchError
        ? error.message
        : error instanceof InventoryScanError && error.code === 'INSUFFICIENT_STOCK'
          ? error.message
          : error instanceof Error
            ? error.message
            : t('NLSHelperToastInventoryScanFailed')
      showToast(message, 'error')
    }
  }, [currentUser, scanStock, scannerItem, showToast, t])

  const showDock = !detailsItem
  const headerAvatarLabel = helperName.slice(0, 1).toUpperCase()
  const closeProfile = () => {
    setIsProfileOpen(false)
    if (typeof window !== 'undefined' && window.location.pathname === '/helper/profile') {
      window.history.pushState({}, '', PATH_BY_TAB[activeTab])
    }
  }


  const focusedChatItem = useMemo(() => resolveFocusedChatItem({
    chatActionItemId,
    detailsItem,
    ghostFocusItemId,
    activeWorkspaceId: snapshot.activeWorkspaceId,
    items: effectiveFeedItems,
  }), [chatActionItemId, detailsItem, ghostFocusItemId, snapshot.activeWorkspaceId, effectiveFeedItems])

  const requireFocusedChatItem = () => {
    if (!focusedChatItem || !actor) {
      showToast(t('NLSHelperChatFocusRequired'), 'ghost')
      return null
    }
    return focusedChatItem
  }

  const helperChatQuickActions = {
    focusedLabel: focusedChatItem?.title,
    items: taskFeed.items
      .filter((item) => item.workspaceId === snapshot.activeWorkspaceId)
      .map((item) => ({
        id: item.id,
        title: item.title,
        subtitle: item.workflowStatus ?? item.boardStatus ?? null,
      })),
    selectedItemId: focusedChatItem?.id ?? null,
    onSelectItem: setChatActionItemId,
    onEvidence: async (file: File) => {
      const item = requireFocusedChatItem()
      if (!item || !actor) return
      await queueChecklistProof({
        workspaceId: item.workspaceId,
        checklistItemId: item.id,
        fileName: file.name || 'evidence',
        contentType: file.type || 'application/octet-stream',
        actorId: actor.id,
        actorName: actor.name,
        assignedAt: item.updatedAtIso ?? null,
      }, file)
      showToast(t('NLSHelperToastEvidenceQueued'), 'success')
    },
    onScan: () => {
      const item = requireFocusedChatItem()
      if (!item) return
      setScannerItem(item)
    },
    onDone: async () => {
      const item = requireFocusedChatItem()
      if (!item || !actor) return
      await queueChecklistToggle({
        workspaceId: item.workspaceId,
        goalId: item.parentContainerId ?? '',
        taskId: null,
        subTaskId: null,
        checklistItemId: item.id,
        isCompleted: true,
        workflowStatus: 'COMPLETED',
        actorId: actor.id,
        actorName: actor.name,
        assignedAt: item.updatedAtIso ?? null,
      })
      showToast(t('NLSHelperToastDoneQueued'), 'success')
    },
    onComment: async (body: string) => {
      const item = requireFocusedChatItem()
      if (!item || !actor) return
      await queueChecklistComment({
        workspaceId: item.workspaceId,
        checklistItemId: item.id,
        parentContainerId: item.parentContainerId ?? item.id,
        body,
        actorId: actor.id,
        actorName: actor.name,
        assignedAt: item.updatedAtIso ?? null,
      })
      showToast(t('NLSHelperToastCommentQueued'), 'success')
    },
  }

  return (
    <div
      ref={shellRef}
      data-helper-shell
      className="h-[100dvh] min-h-[100svh] overflow-hidden bg-[var(--bg)] text-[var(--text-strong)] flex flex-col"
      style={{
        paddingBottom: showDock && !isProfileOpen ? '0px' : 'env(safe-area-inset-bottom)',
        ['--helper-bottom-chrome-height' as string]: showDock && !isProfileOpen
          ? measuredBottomChromeHeight !== null
            ? `${Math.ceil(measuredBottomChromeHeight)}px`
            : '3.625rem'
          : '0px',
      }}
    >
      {!isSharedDevice && <GhostEchoListener onApply={applyGhostEcho} />}
      <KeyboardHeightBridge />
      {!isSharedDevice && (
        <HelperWidgetCachePublisher
          workspaceId={snapshot.activeWorkspaceId}
          workspaceName={snapshot.workspaces.find((workspace) => workspace.id === snapshot.activeWorkspaceId)?.name ?? null}
          items={taskFeed.items}
        />
      )}
      {!isSharedDevice && <RaidCelebration workspaceId={helperFunPackOff ? null : snapshot.activeWorkspaceId} />}
      {!isSharedDevice && !helperFunPackOff && (
        <RaidOverlay open={raidOverlayOpen} onClose={() => setRaidOverlayOpen(false)} />
      )}
      <FocusModeOverlay />
      <HelperFirstRunLanguageModal
        open={showFirstRunLanguagePrompt}
        currentLocale={activeNlsLocale}
        onSelect={completeFirstRunLanguageSelection}
        onSkip={skipFirstRunLanguageSelection}
      />
      <header
        className="sticky top-0 z-30 bg-[color-mix(in_srgb,var(--bg)_85%,transparent)] backdrop-blur-3xl border-b border-[var(--border-subtle)] px-3 py-3 flex items-center gap-2"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        {!isSharedDevice ? (
          <HeaderIconButton
            onClick={() => setMenuOpen(true)}
            label={t('NLSHelperAriaOpenMenu')}
            testId="helper-menu-trigger"
          >
            <MenuRoundedIcon style={{ fontSize: '22px' }} />
          </HeaderIconButton>
        ) : null}
        {!isSharedDevice ? (
          <WorkspaceSwitcher
            workspaces={snapshot.workspaces}
            activeWorkspaceId={snapshot.activeWorkspaceId}
            pending={snapshot.pending}
            onOpenIncomingInvites={() => setIncomingInvitesOpen(true)}
          />
        ) : null}
        <HelperSyncPill onOpenSyncCenter={openSyncCenter} className="shrink-0" />
        {activeScreenGuideId ? (
          <ScreenGuideHost
            screenId={activeScreenGuideId}
            disabled={isTourActive}
            className="screenGuideLauncher--topbar"
          />
        ) : null}
        {!isSharedDevice ? (
          <HeaderIconButton
            onClick={() => setNotificationsOpen(true)}
            label={t('NLSHelperAriaOpenNotifications')}
            unreadLabel={t('NLSHelperAriaUnread')}
            showDot={!focusMode.active && snapshot.unreadNotificationCount > 0}
            testId="helper-notifications-trigger"
          >
            {!focusMode.active && snapshot.unreadNotificationCount > 0 ? (
              <NotificationsActiveRoundedIcon
                style={{ fontSize: '22px' }}
              />
            ) : (
              <NotificationsNoneRoundedIcon style={{ fontSize: '22px' }} />
            )}
          </HeaderIconButton>
        ) : null}
        {isSharedDevice ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void handleSignOut()}
            data-testid="helper-switch-person-btn"
          >
            {t('NLSOpsSharedDeviceSwitchPerson')}
          </Button>
        ) : null}
      </header>

      {(!isSharedDevice || !deviceWorkspaceId) && snapshot.workspaces.length === 0 && !snapshot.workspacesLoading ? (
        <main
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain grid place-items-center px-6"
          style={{
            paddingBottom: 'calc(var(--helper-bottom-chrome-height, 5.75rem) + 1rem + env(safe-area-inset-bottom, 0px))',
            scrollPaddingBottom: 'calc(var(--helper-bottom-chrome-height, 5.75rem) + 1rem)',
          }}
        >
          <div className="text-center flex flex-col items-center gap-3 max-w-xs">
            <div className="h-20 w-20 rounded-full bg-[var(--surface-card)] border border-[var(--border-subtle)] grid place-items-center text-3xl">
              <HourglassEmptyRoundedIcon style={{ fontSize: '32px', color: 'var(--muted)' }} />
            </div>
            <h2 className="text-lg font-semibold text-[var(--text-strong)]">{t('NLSHelperWaitingTitle')}</h2>
            <p className="text-sm text-[var(--muted)]">
              {t('NLSHelperWaitingBody')}
            </p>
            <Button
              variant="tertiary"
              size="md"
              onClick={() => void handleSignOut()}
              className="mt-2"
            >
              {isSharedDevice ? t('NLSOpsSharedDeviceSwitchPerson') : t('NLSCommonActionSignOut')}
            </Button>
          </div>
        </main>
      ) : isAppAccessBlocked ? (
        <HelperAppAccessBlockedNotice
          workspaceName={currentWorkspaceSummary?.name || 'Workspace'}
        />
      ) : (
        <main
          className={
            activeTab === 'CHAT'
              ? 'min-h-0 flex-1 overflow-hidden flex flex-col p-0 gap-0'
              : 'min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 flex flex-col gap-4'
          }
          style={{
            paddingBottom:
              activeTab === 'CHAT'
                ? 'var(--helper-bottom-chrome-height, 3.625rem)'
                : 'calc(var(--helper-bottom-chrome-height, 5.75rem) + 1rem + env(safe-area-inset-bottom, 0px))',
            scrollPaddingBottom:
              activeTab === 'CHAT'
                ? 'var(--helper-bottom-chrome-height, 3.625rem)'
                : 'calc(var(--helper-bottom-chrome-height, 5.75rem) + 1rem)',
          }}
        >
          {!isSharedDevice && !isProfileOpen && activeTab === 'TASKS' ? (
            <HelperApprovalsStrip
              items={taskFeed.items}
              actor={actor ? { uid: actor.id, displayName: actor.name } : null}
              currentUserId={currentUser?.uid ?? null}
            />
          ) : null}
          {!isSharedDevice && !isProfileOpen && activeTab === 'TASKS' ? (
            <TabErrorBoundary tabName="Tasks">
              <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar snap-x snap-mandatory py-1">
                {(['ACTIVE', 'UPCOMING', 'PAST', 'ALL'] as const).map((key) => {
                  const isSelected = taskFilter === key
                  const count = key === 'ACTIVE' ? taskCounts.active
                    : key === 'UPCOMING' ? taskCounts.upcoming
                    : key === 'PAST' ? taskCounts.past
                    : taskCounts.all
                  const labels: Record<typeof key, string> = {
                    ACTIVE: 'Active',
                    UPCOMING: 'Upcoming',
                    PAST: 'Past',
                    ALL: 'All',
                  }
                  return (
                    <Pressable
                      key={key}
                      onClick={() => setTaskFilter(key)}
                      aria-pressed={isSelected}
                      className={`snap-start shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                        isSelected
                          ? 'bg-[var(--accent-soft)] text-[var(--accent-strong)] border border-[var(--accent-soft)] shadow-sm'
                          : 'bg-[var(--surface-muted)] text-[var(--muted)] border border-[var(--border-subtle)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-strong)]'
                      }`}
                    >
                      <span>{labels[key]}</span>
                      <span className={`px-1.5 py-0.2 rounded-full text-[10px] bg-[var(--surface-card)] ${
                        count === 0 ? 'text-[var(--muted)]' : 'text-[var(--text-strong)]'
                      }`}>
                        {count}
                      </span>
                    </Pressable>
                  )
                })}
              </div>
              <TaskCarousel
                items={filteredTasks}
                loading={taskFeed.loading}
                workspaces={snapshot.workspaces}
                focusItemId={ghostFocusItemId}
                onActiveItemChange={(item) => { currentCarouselItemRef.current = item }}
                onOpenDetails={setDetailsItem}
                emptyStateTitle={
                  taskFilter === 'ACTIVE'
                    ? 'No active tasks today'
                    : taskFilter === 'UPCOMING'
                      ? 'No upcoming tasks'
                      : taskFilter === 'PAST'
                        ? 'No past tasks'
                        : undefined
                }
                emptyStateBody={
                  taskFilter === 'ACTIVE' && (taskCounts.upcoming > 0 || taskCounts.past > 0)
                    ? `You have ${taskCounts.upcoming} upcoming and ${taskCounts.past} past tasks.`
                    : filteredTasks.length === 0 && taskFeed.items.length > 0
                      ? 'No tasks found for this filter.'
                      : undefined
                }
                emptyStateAction={
                  filteredTasks.length === 0 && taskFeed.items.length > 0 && taskFilter !== 'ALL'
                    ? (
                        <Button
                          variant="secondary"
                          size="md"
                          onClick={() => setTaskFilter('ALL')}
                          className="mt-2"
                        >
                          Show all tasks
                        </Button>
                      )
                    : undefined
                }
                onQuickAction={(item, kind, payload) => {
                  if (!actor) return
                  if (kind === 'PROOF' && payload.file) {
                    void submitHelperProof({
                      item: {
                        id: item.id,
                        workspaceId: item.workspaceId,
                        title: item.title,
                        updatedAtIso: item.updatedAtIso ?? null,
                      },
                      actor,
                      file: payload.file,
                      actorRole: 'HELPER',
                    }).catch((error) => console.warn('[quick-action] proof failed', error))
                  }
                  if (kind === 'LOCATION' && payload.coords) {
                    import('../../debug')
                      .then(({ isNoSignInPreviewActive, recordDebugLocationCheckIn }) => {
                        if (isNoSignInPreviewActive()) {
                          recordDebugLocationCheckIn({
                            workspaceId: item.workspaceId,
                            itemId: item.id,
                            itemTitle: item.title,
                            actorId: actor.id,
                            actorName: actor.name,
                            coords: payload.coords as GeolocationCoordinates,
                          })
                        } else {
                          setDetailsItem(item)
                        }
                      })
                      .catch(() => setDetailsItem(item))
                  }
                }}
                readinessByItemId={readinessByItemId}
                onReportBlocker={(item) => {
                  setBlockerError(null)
                  setBlockerTarget({ item, mode: 'BLOCKER' })
                }}
                onRequestReplenishment={(item) => {
                  setBlockerError(null)
                  setBlockerTarget({ item, mode: 'REPLENISHMENT' })
                }}
              />
            </TabErrorBoundary>
          ) : null}
          {!isSharedDevice && !isProfileOpen && activeTab === 'CALENDAR' ? (
            <TabErrorBoundary tabName="Calendar">
              <HelperCalendar
                items={taskFeed.items}
                meetings={helperMeetings}
                onSelectItem={(item, occurrenceKey) => {
                  setDetailsOccurrenceKey(occurrenceKey ?? null)
                  setDetailsItem(item)
                }}
              />
            </TabErrorBoundary>
          ) : null}
          {!isSharedDevice && !isProfileOpen && activeTab === 'MEETINGS' ? (
            <TabErrorBoundary tabName="Meetings">
              <MeetingActionGateway
                workspaceId={snapshot.activeWorkspaceId}
                uid={currentUser?.uid ?? null}
                onOpenAgendaNode={openItemById}
              />
            </TabErrorBoundary>
          ) : null}
          {!isSharedDevice && !isProfileOpen && activeTab === 'CHAT' ? (
            <TabErrorBoundary tabName="Chat">
              <MemberChatScreen
                helperMode
                onElementMentionClick={openItemById}
                helperSyncState={snapshot.syncState}
                helperQuickActions={helperChatQuickActions}
                renderHelperSummary={(messages: Message[], channelId: string) => snapshot.activeWorkspaceId ? (
                  <AISummaryBubble
                    workspaceId={snapshot.activeWorkspaceId}
                    channelId={channelId}
                    messages={messages.map((message) => ({
                      authorName: message.authorName,
                      text: message.textContent,
                      timestamp: null,
                    }))}
                    auto
                  />
                ) : null}
                onTranslateMessage={(message: Message) => snapshot.activeWorkspaceId
                  ? translateMessageForHelper({
                      workspaceId: snapshot.activeWorkspaceId,
                      messageId: message.id,
                      text: message.textContent,
                      targetLanguage: getHelperLanguagePreference(),
                    })
                  : Promise.resolve(message.textContent)}
              />
            </TabErrorBoundary>
          ) : null}
          {!isProfileOpen && activeTab === 'FIELD' && effectiveActiveWorkspaceId ? (
            <TabErrorBoundary tabName="Field">
              <HelperFieldTab
                workspaceId={effectiveActiveWorkspaceId}
                workspaces={snapshot.workspaces}
                workspace={fullWorkspaces.find((w) => w.id === effectiveActiveWorkspaceId) ?? null}
                items={effectiveFeedItems}
                actor={actor}
                subtab={fieldSubtab}
                visibleSubtabs={visibleFieldSubtabs}
                onSubtabChange={setFieldSubtab}
                readinessByItemId={readinessByItemId}
                openBlockers={openBlockers}
                onOpenItem={(itemId) => {
                  openItemById(itemId)
                  if (!isSharedDevice) {
                    setActiveTab('TASKS')
                  }
                }}
                unsentCount={unsentCount}
                workspaceName={snapshot.workspaces.find((w) => w.id === effectiveActiveWorkspaceId)?.name}
                hideSubtabs
              />
            </TabErrorBoundary>
          ) : null}
          {!isProfileOpen && activeTab === 'FIELD' && !effectiveActiveWorkspaceId ? (
            <section className="flex-1 grid place-items-center rounded-3xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-6 text-center">
              <p className="text-sm text-[var(--muted)]">{t('NLSHelperPickWorkspaceBeforeScan')}</p>
            </section>
          ) : null}
          {!isSharedDevice && !isProfileOpen && activeTab === 'SCHOOL' && snapshot.activeWorkspaceId ? (
            <TabErrorBoundary tabName="School">
              <SchoolTab
                workspaceId={snapshot.activeWorkspaceId}
                workspaceName={snapshot.workspaces.find((w) => w.id === snapshot.activeWorkspaceId)?.name}
              />
            </TabErrorBoundary>
          ) : null}
          {!isProfileOpen && activeTab === 'OPS' && effectiveActiveWorkspaceId ? (
            <TabErrorBoundary tabName="Ops">
              <HelperOpsTab
                workspaceId={effectiveActiveWorkspaceId}
                isSharedDevice={isSharedDevice}
                devicePlaceId={isSharedDevice ? getStoredSharedDeviceCredentials()?.placeId : undefined}
              />
            </TabErrorBoundary>
          ) : null}
          {debugPanel ?? null}
        </main>
      )}

      {showDock && !isProfileOpen && !isAppAccessBlocked ? (
        <HelperBottomNav
          activeTab={activeTab}
          onChange={setActiveTab}
          /* hiddenTabs={moduleHidden.helperTabs} */
          hiddenTabs={isSharedDevice ? new Set<HelperTabKey>(['TASKS', 'CALENDAR', 'MEETINGS', 'CHAT', 'SCHOOL', ...moduleHidden.helperTabs]) : moduleHidden.helperTabs}
          uid={isSharedDevice ? undefined : currentUser?.uid}
          workspaceId={effectiveActiveWorkspaceId ?? undefined}
          onEnterFocus={() => {
            emitSensoryEvent('MUTATION_TRIGGERED', { source: 'helper-todos-tab' })
            focusMode.enter()
          }}
          unreadByTab={unreadByTab}
          onHeightChange={handleBottomNavHeightChange}
          secondaryTabs={
            activeTab === 'FIELD' && effectiveActiveWorkspaceId ? (
              <HelperFieldSubtabStrip
                subtabs={visibleFieldSubtabs}
                active={fieldSubtab}
                onChange={setFieldSubtab}
                t={t}
                placement="bottom"
              />
            ) : undefined
          }
        />
      ) : null}

      {!isSharedDevice && (
        <HelperMenuDrawer
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          displayName={helperName}
          identity={null}
          avatarUrl={profileImageUrl || null}
          snapshot={snapshot}
          onOpenSettings={() => {
            setSettingsInitialSection(undefined)
            setSettingsOpen(true)
          }}
          onOpenProfile={() => {
            setIsProfileOpen(true)
            if (typeof window !== 'undefined' && window.location.pathname !== '/helper/profile') {
              window.history.pushState({}, '', '/helper/profile')
            }
          }}
          onOpenIncomingInvites={() => setIncomingInvitesOpen(true)}
          focusModeActive={focusMode.active}
          onEnterFocus={() => {
            if (activeTab !== 'TASKS') {
              focusMode.enter()
              return
            }
            const current = currentCarouselItemRef.current ?? filteredTasks[0] ?? taskFeed.items[0]
            focusMode.enter(current ?? undefined)
          }}
          onOpenOverview={canPerform('overview.view') ? () => setOverviewOpen(true) : undefined}
          onOpenForms={() => setIsFormsOpen(true)}
          onSignOut={() => void handleSignOut()}
        />
      )}
      {!isSharedDevice && isSyncCenterOpen ? (
        <MobileFullScreenPanel
          title={t('NLSHelperSubtabSync')}
          uiName="UI-MPCAP-HSYNC"
          onClose={() => setIsSyncCenterOpen(false)}
        >
          <MobileSyncCenter
            role="HELPER"
            onOpenItem={(itemId) => {
              setIsSyncCenterOpen(false)
              openItemById(itemId)
              setActiveTab('TASKS')
            }}
          />
        </MobileFullScreenPanel>
      ) : null}
      {!isSharedDevice && isFormsOpen && snapshot.activeWorkspaceId ? (
        <MobileFullScreenPanel
          title={t('NLSHelperFormsTitle')}
          uiName="UI-MPCAP-HFORMS"
          onClose={() => setIsFormsOpen(false)}
        >
          <HelperFormsPanel
            workspaceId={snapshot.activeWorkspaceId}
            actor={actor}
            onOpenItem={(itemId) => {
              setIsFormsOpen(false)
              openItemById(itemId)
              setActiveTab('TASKS')
            }}
          />
        </MobileFullScreenPanel>
      ) : null}
      {!isSharedDevice && overviewOpen ? (
        <MobileFullScreenPanel
          title={t('NLSHelperPanelOverview')}
          uiName="UI-MPCAP-HOVR"
          screenGuideId="helper.overview"
          onClose={() => setOverviewOpen(false)}
        >
          <OverviewPhonePage variant="helper" onBack={() => setOverviewOpen(false)} />
        </MobileFullScreenPanel>
      ) : null}
      {!isSharedDevice && incomingInvitesOpen ? (
        <MobileFullScreenPanel
          title={t('NLSHelperPanelIncomingInvites')}
          uiName="UI-MPCAP-HINV"
          screenGuideId="helper.incoming-invites"
          onClose={() => setIncomingInvitesOpen(false)}
        >
          <IncomingInvitesPage roleHint="HELPER" />
        </MobileFullScreenPanel>
      ) : null}
      {!isSharedDevice && isProfileOpen ? (
        <MobileFullScreenPanel
          title={t('NLSHelperPanelProfile')}
          eyebrow={t('NLSHelperPanelProfileEyebrow')}
          uiName="UI-MPCAP-HPRO"
          screenGuideId="helper.profile"
          onClose={closeProfile}
        >
          <HelperProfilePanel />
        </MobileFullScreenPanel>
      ) : null}
      {!isSharedDevice && (
        <HelperNotificationDrawer
          open={notificationsOpen}
          onClose={() => setNotificationsOpen(false)}
          uid={currentUser?.uid ?? null}
          onNavigate={handleNotificationNavigate}
          onOpenNotificationSettings={() => {
            setNotificationsOpen(false)
            setSettingsInitialSection('ALERTS')
            setSettingsOpen(true)
          }}
          hide={hideNotificationItem}
        />
      )}
      {!isSharedDevice && settingsOpen ? (
        <MobileFullScreenPanel
          title={t('NLSHelperPanelSettings')}
          eyebrow={t('NLSHelperPanelCommandPreferences')}
          uiName="UI-MPCAP-HSET"
          screenGuideId="helper.settings"
          onClose={() => setSettingsOpen(false)}
        >
          <MobileSettingsCommandCenter
            role="HELPER"
            displayName={helperName}
            workspaceId={activeHelperWorkspace?.id ?? null}
            workspaceName={activeHelperWorkspace?.name ?? null}
            workspaceRole={activeHelperWorkspace?.role ?? 'HELPER'}
            helperAppLocale={activeHelperWorkspace?.helperAppLocale ?? null}
            notifyVia={activeHelperWorkspace?.notifyVia ?? 'BOTH'}
            workspaceCount={snapshot.workspaces.length}
            pendingSyncCount={snapshot.pending.length}
            failedSyncCount={snapshot.failed.length}
            unreadCount={snapshot.unreadNotificationCount}
            onHelperAppLocaleChange={handleHelperAppLocaleChange}
            onOpenProfile={() => {
              setSettingsOpen(false)
              setIsProfileOpen(true)
              if (typeof window !== 'undefined' && window.location.pathname !== '/helper/profile') {
                window.history.pushState({}, '', '/helper/profile')
              }
            }}
            onOpenIncomingInvites={() => {
              setSettingsOpen(false)
              setIncomingInvitesOpen(true)
            }}
            onSignOut={() => void handleSignOut()}
            initialSection={settingsInitialSection}
          />
        </MobileFullScreenPanel>
      ) : null}
      {detailsItem ? (
        <ChecklistDetailsModal
          item={detailsItem}
          actor={actor}
          blockedTodoPolicy={snapshot.workspaces.find((workspace) => workspace.id === detailsItem.workspaceId)?.checklistBlockedTodoPolicy}
          initialOccurrenceKey={detailsOccurrenceKey}
          readiness={readinessByItemId.get(detailsItem.id) ?? null}
          onReportBlocker={(item) => {
            setBlockerError(null)
            setBlockerTarget({ item, mode: 'BLOCKER' })
          }}
          onRequestReplenishment={(item) => {
            setBlockerError(null)
            setBlockerTarget({ item, mode: 'REPLENISHMENT' })
          }}
          onClose={() => {
            setDetailsOccurrenceKey(null)
            setDetailsItem(null)
          }}
        />
      ) : null}
      <BlockerReportSheet
        open={Boolean(blockerTarget)}
        mode={blockerTarget?.mode ?? 'BLOCKER'}
        readiness={blockerTarget ? readinessByItemId.get(blockerTarget.item.id) ?? null : null}
        busy={blockerBusy}
        error={blockerError}
        onClose={closeBlockerSheet}
        onSubmit={(input) => void submitBlockerSheet(input)}
      />
      <BarcodeScannerShell
        open={Boolean(scannerItem)}
        onClose={() => setScannerItem(null)}
        onScan={(barcode, action) => void handleInventoryScan(barcode, action)}
      />
      <ExitConfirmSheet
        open={exitConfirmOpen}
        onClose={() => setExitConfirmOpen(false)}
      />
      <ExitConfirmSheet
        open={unsentConfirmOpen}
        onClose={() => setUnsentConfirmOpen(false)}
        onExit={() => void handleConfirmSignOutWithUnsent()}
        title={t('NLSHelperSignOutUnsentTitle')}
        description={t('NLSHelperSignOutUnsentBody', { count: unsentCount })}
        confirmLabel={t('NLSCommonActionSignOut')}
        cancelLabel={t('NLSHelperSignOutCancel')}
      />

      <span className="sr-only" aria-live="polite">
        {headerAvatarLabel}
      </span>
    </div>
  )
}
